// src/config/moduleTypes.js
//
// Single source of truth for "module type" (mental / fysisk / forstehjelp / securitas /
// sykefravaer) — the category that drives which backend template endpoints to call, what
// color/label to show, which theory/test/training features are supported, and which
// checkboxes get locked in the template-creation form.
//
// Do NOT confuse this with a "module" (course module / module instance) like ABCDE, NEWS2,
// ISBAR, etc. — those live in ./moduleThumbnails.js. Every module belongs to exactly one
// module type.
//
// Adding a 6th module type: add one entry below with its endpoints/support/color/labels. That
// is the only file that needs a code change — every consumer in this app reads this registry
// instead of branching on the type name. See MODULE_ARCHITECTURE_PLAN.md for the full rationale
// and migration history.
import {
  AddTemplateMental, GetMentalTemplates, GetMentalTemplateDetails, DeleteMentalTemplate,
  AddTemplateFysisk, GetFysiskTemplates, GetFysiskTemplateDetails, DeleteFysiskTemplate,
  AddTemplateForstehjelp, GetForstehjelpTemplates, GetForstehjelpTemplateDetails, DeleteForstehjelpTemplate,
  AddTemplateSecuritas, GetSecuritasTemplate, GetSecuritasTemplateDetails, DeleteSecuritasTemplate,
  AddTemplateSykefravaer, GetSykefravaerTemplate, GetSykefravaerTemplateDetails, DeleteSykefravaerTemplate,
} from "../api/endpoints";

export const MODULE_TYPES = {
  mental: {
    key: "mental",
    aliases: [],
    // Key inside CourseTemplateAvailabilityResponseDto.data (from /api/Courses/CheckAvailableTemplates).
    availabilityKey: "mental",
    packageName: "VR Safe",
    categoryName: "Mental Helse",
    color: "#00CFFF ",
    endpoints: { add: AddTemplateMental, getAll: GetMentalTemplates, getDetails: GetMentalTemplateDetails, delete: DeleteMentalTemplate },
    support: { theory: true, test: true, trainingPercentage: true },
    // Checkbox behavior in TemplateCreationModal when this type is selected. No locks/forced
    // values for the three "normal" types (mental, fysisk, forstehjelp).
    checkboxLocks: { teori: false, test: false, trening: false },
    forcedCheckboxValues: null,
  },
  fysisk: {
    key: "fysisk",
    aliases: [],
    availabilityKey: "fysisk",
    packageName: "VR Obs",
    categoryName: "Fysisk Helse",
    color: "#C185B2 ",
    endpoints: { add: AddTemplateFysisk, getAll: GetFysiskTemplates, getDetails: GetFysiskTemplateDetails, delete: DeleteFysiskTemplate },
    support: { theory: true, test: true, trainingPercentage: true },
    checkboxLocks: { teori: false, test: false, trening: false },
    forcedCheckboxValues: null,
  },
  forstehjelp: {
    key: "forstehjelp",
    aliases: ["førstehjelp"],
    // NB: the backend's CheckAvailableTemplates response uses "forsteHjelp" (capital H) here,
    // not "forstehjelp" — this mismatch used to be spread across every call site by hand
    // (see MODULE_ARCHITECTURE_PLAN.md §6); now it's declared once, in one place.
    availabilityKey: "forsteHjelp",
    packageName: "VR Aid",
    categoryName: "Førstehjelp",
    color: "#FA5C5C ",
    endpoints: { add: AddTemplateForstehjelp, getAll: GetForstehjelpTemplates, getDetails: GetForstehjelpTemplateDetails, delete: DeleteForstehjelpTemplate },
    support: { theory: true, test: true, trainingPercentage: true },
    checkboxLocks: { teori: false, test: false, trening: false },
    forcedCheckboxValues: null,
  },
  securitas: {
    key: "securitas",
    aliases: [],
    availabilityKey: "securitas",
    packageName: "Securitas",
    categoryName: "Securitas",
    color: "#FC263E ",
    endpoints: { add: AddTemplateSecuritas, getAll: GetSecuritasTemplate, getDetails: GetSecuritasTemplateDetails, delete: DeleteSecuritasTemplate },
    support: { theory: false, test: false, trainingPercentage: false },
    // Securitas templates only ever run as "trening" — teori/test are locked off and forced off,
    // trening is forced on. Matches the original hand-written branch in TemplateCreationModal.
    checkboxLocks: { teori: true, test: true, trening: false },
    forcedCheckboxValues: { teori: false, test: false, trening: true },
  },
  sykefravaer: {
    key: "sykefravaer",
    aliases: ["sykefravær"],
    availabilityKey: "sykefravaer",
    packageName: "Sykefravær",
    categoryName: "Sykefravær",
    color: "#FC263E ",
    endpoints: { add: AddTemplateSykefravaer, getAll: GetSykefravaerTemplate, getDetails: GetSykefravaerTemplateDetails, delete: DeleteSykefravaerTemplate },
    support: { theory: true, test: false, trainingPercentage: false },
    // Sykefravaer templates don't support "test" — it's locked and forced off. Teori/trening are
    // left alone (whatever the user already had toggled), matching the original behavior.
    checkboxLocks: { teori: false, test: true, trening: false },
    forcedCheckboxValues: { test: false },
  },
};

export const MODULE_TYPE_LIST = Object.values(MODULE_TYPES);

/**
 * Normalizes any casing/alias variant ("Førstehjelp", "FORSTEHJELP", "sykefravær", …) to its
 * canonical registry entry. Returns undefined for anything unrecognized instead of throwing —
 * callers decide how to handle an unknown type (render nothing, log a warning, etc.) rather than
 * crashing the whole screen the way some call sites used to.
 */
export function resolveModuleType(moduleType) {
  const needle = (moduleType ?? "").toLowerCase();
  return MODULE_TYPE_LIST.find((t) => t.key === needle || t.aliases.includes(needle));
}
